import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { SlaRunStateIcon } from '@components/ui/sla-timer';
import { SimpleTooltipV2 } from '@components/ui/tooltip';
import { AlertCircle, AlertTriangle, Link2, UserRoundCog } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { WorkflowType } from '@/constants/dispositions';
import { useBelowProgramAdminUsers } from '@/hooks/useBelowProgramAdminUsers';
import { cn } from '@/utils';
import { buildRoutePath } from '@/utils/routes';

import { formatCallAttempts, formatSla, formatStage } from '../data/formatters';
import { programAdminOnlyPrograms, reassignableForCases } from '../data/humanAuditAssignees';
import { canReassignCase } from '../data/reassign';
import type { Agent, CaseItem } from '../data/types';
import { ASSIGNED_TO_ME_ID } from '../data/types';
import { copyCaseLink } from './copyCaseLink';
import { PayerHoursPopover } from './PayerHoursPopover';
import { ReassignConfirmDialog } from './ReassignConfirmDialog';
import { ReassignPopover } from './ReassignPopover';

interface CaseCardProps {
  caseItem: CaseItem;
  agents: Agent[];
  /** Real-user representation of the logged-in user (with their actual
   *  user_id). Included in the reassign dropdown when the user is a member
   *  of the case's team — covers the "let me reassign this to myself" path
   *  that's otherwise blocked because the synthetic "Assigned to me" row
   *  uses an invalid user_id. */
  currentUserAgent: Agent | null;
  /** Returns a promise that resolves when the reassign settles (success or failure). */
  onReassign: (caseId: string, newAgentId: string) => Promise<void>;
  /** Mutation in flight — disables interactive controls + locks the confirm dialog. */
  isReassigning: boolean;
}

// Base card chrome (background, border colour). The risk tier is layered
// on top via `borderAccentByRisk` — a 4px left-edge stripe in the risk
// colour, with healthy intentionally muted so the common case stays quiet.
const CARD_BASE_STYLE = 'bg-muted/40 border-border';

const borderAccentByRisk: Record<CaseItem['riskLevel'], string> = {
  breached: 'border-l-4 border-l-critical-foreground',
  warning: 'border-l-4 border-l-attention-foreground',
  healthy: 'border-l-4 border-l-positive-foreground/40',
};

// Short card labels per workflow_type. Unknown types fall back to "Med BV"
// since the MedBV family (medbv / unifiedmedbv / variants) is the historical
// default and shares the same display convention. Keyed by the `WorkflowType`
// enum (mirrors how `extractPayerSlug` references workflow types) so renames
// surface as typecheck errors rather than silent label loss.
const WORKFLOW_LABELS: Partial<Record<WorkflowType, string>> = {
  [WorkflowType.PBM]: 'Pharm BV',
  [WorkflowType.TRANSPORTATION]: 'Transport BV',
  [WorkflowType.PA_STATUS_PBM]: 'PA Status Pharm BV',
  [WorkflowType.PA_STATUS_MEDBV]: 'PA Status Med BV',
};

function workflowLabel(workflowType: string | undefined): string {
  // `workflow_type` is free-form `string` on the API; cast at the index so
  // unknown values flow through to the `'Med BV'` fallback below.
  return WORKFLOW_LABELS[workflowType as WorkflowType] ?? 'Med BV';
}

const slaToneByRisk: Record<CaseItem['riskLevel'], string> = {
  breached: 'text-critical-foreground',
  warning: 'text-attention-foreground',
  healthy: 'text-positive-foreground',
};

export const CaseCard = React.memo(function CaseCard({
  caseItem,
  agents,
  currentUserAgent,
  onReassign,
  isReassigning,
}: CaseCardProps) {
  const [reassignOpen, setReassignOpen] = useState(false);
  const [pendingTargetAgentId, setPendingTargetAgentId] = useState<string | null>(null);
  const slaTone = slaToneByRisk[caseItem.riskLevel];

  const canOpenCase = !!caseItem.id && !!caseItem.organizationId && !!caseItem.programId;
  // Open the case-details page in a new tab. `noopener,noreferrer` strips the
  // window-opener reference (HIPAA-safe — the new tab can't read this one's
  // location / contents) and prevents `Referer` leak.
  const openCase = () => {
    if (!canOpenCase) return;
    const url = buildRoutePath(
      `/cases/${caseItem.id}`,
      caseItem.organizationId!,
      caseItem.programId!,
    );
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleCopyLink = (e: React.MouseEvent) => {
    e.stopPropagation();
    copyCaseLink(caseItem);
  };

  // Reassignment offers every member of the selected teams (the agent rows).
  // The synthetic "Assigned to me" row is dropped (its id `'me'` isn't a valid
  // user_id); the logged-in user is added back via `currentUserAgent` (their
  // real user_id) so the manager can reassign to themselves. We do not pre-filter
  // by the case's own team: `useWorkforceReassign` picks a best-effort target
  // team and the backend validates the move (a rejected move shows an error toast).
  const teamAgents: Agent[] = useMemo(() => {
    const list: Agent[] = [];
    if (currentUserAgent) list.push(currentUserAgent);
    for (const a of agents) {
      if (a.id === ASSIGNED_TO_ME_ID) continue;
      if (currentUserAgent && a.id === currentUserAgent.id) continue;
      list.push(a);
    }
    return list;
  }, [agents, currentUserAgent]);

  // A transportation or unifiedmedbv case in human_audit can be assigned only to a Program
  // Admin or a higher role (BV-2657), so users below that on the case's program are left out.
  const reservedPrograms = useMemo(() => programAdminOnlyPrograms([caseItem]), [caseItem]);
  const teamAgentIds = useMemo(() => teamAgents.map(a => a.id), [teamAgents]);
  const { isBelow } = useBelowProgramAdminUsers({
    organizationId: caseItem.organizationId,
    programIds: reservedPrograms,
    userIds: teamAgentIds,
    enabled: reservedPrograms.length > 0,
  });
  const reassignableAgents: Agent[] = useMemo(
    () => reassignableForCases(teamAgents, reservedPrograms, isBelow),
    [teamAgents, reservedPrograms, isBelow],
  );

  const targetAgent = pendingTargetAgentId
    ? (reassignableAgents.find(a => a.id === pendingTargetAgentId) ?? null)
    : null;
  const currentAssignee = agents.find(a => a.id === caseItem.assigneeId) ?? null;

  const startReassign = (newAgentId: string) => {
    setReassignOpen(false);
    if (newAgentId !== caseItem.assigneeId) {
      setPendingTargetAgentId(newAgentId);
    }
  };

  const confirmReassign = async () => {
    if (!pendingTargetAgentId) return;
    try {
      await onReassign(caseItem.id, pendingTargetAgentId);
    } finally {
      setPendingTargetAgentId(null);
    }
  };

  return (
    <>
      <div
        role={canOpenCase ? 'link' : undefined}
        tabIndex={canOpenCase ? 0 : undefined}
        onClick={canOpenCase ? openCase : undefined}
        onKeyDown={
          canOpenCase
            ? e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openCase();
                }
              }
            : undefined
        }
        aria-label={canOpenCase ? `Open case ${caseItem.payer} ${caseItem.program}` : undefined}
        className={cn(
          'group relative flex flex-col gap-1.5 rounded-md border p-2.5 text-xs shadow-sm transition-colors',
          'min-h-[120px]',
          CARD_BASE_STYLE,
          borderAccentByRisk[caseItem.riskLevel],
          canOpenCase &&
            'cursor-pointer hover:bg-accent/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        )}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="font-semibold tracking-wide text-foreground truncate">
              {caseItem.payer && (
                <>
                  <span>{caseItem.payer}</span>
                  <span className="text-muted-foreground"> · </span>
                </>
              )}
              <span className="text-foreground/90 font-medium">{caseItem.program || '—'}</span>
            </div>
            <div className="text-[11px] italic text-muted-foreground">
              {workflowLabel(caseItem.workflowType)}
              {caseItem.statusLabel && (
                <>
                  <span className="not-italic mx-1">·</span>
                  <span className="not-italic font-medium text-foreground/80">
                    {caseItem.statusLabel}
                  </span>
                </>
              )}
            </div>
            {(caseItem.dispositionLabel ||
              (caseItem.aiCallAttempts ?? 0) > 0 ||
              (caseItem.humanCallAttempts ?? 0) > 0 ||
              (caseItem.missingFieldsCount ?? 0) > 0) && (
              <div className="flex items-center gap-2 mt-2 min-w-0">
                {caseItem.dispositionLabel && (
                  <Badge
                    variant="secondary"
                    className="px-2 py-0.5 text-[10px] font-medium min-w-0 truncate"
                  >
                    {caseItem.dispositionLabel}
                  </Badge>
                )}
                {(caseItem.aiCallAttempts ?? 0) > 0 && (
                  <Badge variant="outline" className="px-2 py-0.5 text-[10px] shrink-0">
                    {formatCallAttempts(caseItem.aiCallAttempts ?? 0, 'AI')}
                  </Badge>
                )}
                {(caseItem.humanCallAttempts ?? 0) > 0 && (
                  <Badge variant="outline" className="px-2 py-0.5 text-[10px] shrink-0">
                    {formatCallAttempts(caseItem.humanCallAttempts ?? 0, 'human')}
                  </Badge>
                )}
                {(caseItem.missingFieldsCount ?? 0) > 0 && (
                  <Badge variant="warning" className="px-2 py-0.5 text-[10px] ml-auto shrink-0">
                    {caseItem.missingFieldsCount} missing
                  </Badge>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {canOpenCase && <PayerHoursPopover caseItem={caseItem} />}
            {canOpenCase && (
              <SimpleTooltipV2 content="Copy case link">
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Copy case link"
                  className={cn(
                    'size-6 transition-opacity',
                    'opacity-0 group-hover:opacity-100 focus-visible:opacity-100',
                  )}
                  onClick={handleCopyLink}
                >
                  <Link2 className="size-3.5" />
                </Button>
              </SimpleTooltipV2>
            )}
            {canReassignCase(caseItem) && (
              <ReassignPopover
                agents={reassignableAgents}
                open={reassignOpen}
                onOpenChange={setReassignOpen}
                onPick={startReassign}
              >
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Reassign case"
                  className={cn(
                    'size-6 transition-opacity',
                    'opacity-0 group-hover:opacity-100 focus-visible:opacity-100',
                    'data-[state=open]:opacity-100',
                  )}
                  onClick={e => e.stopPropagation()}
                >
                  <UserRoundCog className="size-3.5" />
                </Button>
              </ReassignPopover>
            )}
            {caseItem.riskLevel === 'breached' && (
              <AlertCircle
                className="size-3.5 text-critical-foreground"
                aria-label="SLA breached"
              />
            )}
            {caseItem.riskLevel === 'warning' && (
              <AlertTriangle
                className="size-3.5 text-attention-foreground"
                aria-label="At risk of SLA breach"
              />
            )}
          </div>
        </div>

        <div className="mt-auto flex items-center justify-between gap-2 pt-1">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground tabular-nums">
            <span className="font-medium">STAGE</span>{' '}
            <span className="text-foreground/80 font-medium normal-case">
              {formatStage(caseItem.stageMinutes)}
            </span>
          </span>
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground tabular-nums">
            <SlaRunStateIcon paused={caseItem.slaPaused} />
            <span className="font-medium">SLA</span>{' '}
            <span className={cn('font-semibold normal-case', slaTone)}>
              {formatSla(caseItem.slaDeltaMinutes)}
            </span>
          </span>
        </div>
      </div>

      <ReassignConfirmDialog
        open={!!pendingTargetAgentId}
        onOpenChange={openValue => {
          if (!openValue && !isReassigning) setPendingTargetAgentId(null);
        }}
        currentAssigneeName={currentAssignee?.name || 'Unassigned'}
        targetAgentName={targetAgent?.name || ''}
        isPending={isReassigning}
        onConfirm={confirmReassign}
      />
    </>
  );
});
